import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, fromEvent, merge, of, timer } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { LocalAgentService } from './local-agent.service';

const DEFAULT_POLL_MS = 60000;

/**
 * Disponibilidad del CertiTool / Certifirma Agent (loopback).
 * Usado para mostrar u ocultar opciones de token físico en el shell.
 */
@Injectable({ providedIn: 'root' })
export class AgentAvailabilityService implements OnDestroy {
  private readonly availableSubject = new BehaviorSubject<boolean>(false);
  private pollSub: Subscription | null = null;
  private readonly pollMs =
    typeof environment.localAgentHealthPollMs === 'number' && environment.localAgentHealthPollMs > 0
      ? environment.localAgentHealthPollMs
      : DEFAULT_POLL_MS;

  readonly isAvailable$: Observable<boolean> = this.availableSubject
    .asObservable()
    .pipe(distinctUntilChanged());

  constructor(private localAgent: LocalAgentService) {}

  get snapshot(): boolean {
    return this.availableSubject.value;
  }

  /** Arranca chequeo inmediato + polling + recheck al volver a la pestaña. */
  startWatching(): void {
    if (!environment.tokenFisicoEnabled) {
      this.availableSubject.next(false);
      return;
    }
    if (this.pollSub) {
      return;
    }
    const visibility$ =
      typeof document !== 'undefined'
        ? fromEvent(document, 'visibilitychange').pipe(map(() => document.visibilityState === 'visible'))
        : of(true);

    this.pollSub = merge(of(true), timer(this.pollMs, this.pollMs), visibility$)
      .pipe(
        switchMap(() => {
          if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            return of(this.availableSubject.value);
          }
          return this.refreshOnce();
        })
      )
      .subscribe();
  }

  stopWatching(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
  }

  refreshOnce(): Observable<boolean> {
    if (!environment.tokenFisicoEnabled) {
      this.availableSubject.next(false);
      return of(false);
    }
    return this.localAgent.isAvailable().pipe(
      catchError(() => of(false)),
      tap((ok) => this.availableSubject.next(!!ok))
    );
  }

  ngOnDestroy(): void {
    this.stopWatching();
  }
}
